import React, { useState, useEffect, useRef } from 'react'
import { ImageBackground, View, TouchableOpacity, Animated, Platform, Alert, Linking, ActivityIndicator } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Button, Screen, Text, Icon, OnBoarding } from '@components'
import { GenericNavigationProp, ScreenAuthProps } from '@navigation'
import { finalStepRegister, finalStepRegisterWeb } from '@assets'
import { useLanguage } from '@context'
import { useAnimatedValues, useGetProfile, useScreen, useShareMyLocation } from '@hooks'
import getStyle from './style'
import { useCurrentUserStore } from '@store'
import { Coords, getCurrentPosition, showErrorToast } from '@utils'

// On a phone the position can take seconds to arrive after « Autoriser »:
// « Suivant » waits for it this long at most, then opens the app.
const POSITION_MAX_WAIT_MS = 4000

export const FinalStep: React.FC<ScreenAuthProps<'FinalStep'>> = ({ route, navigation }) => {
  const { navigate } = useNavigation<GenericNavigationProp>()

  const { t } = useLanguage()
  const { top } = useSafeAreaInsets()

  const theme = useTheme()
  const { isWeb, isMobile } = useScreen()
  // Web grand écran : grand plan plein écran avec l'image HD (1600×2000) -> nette
  // et immersive. Mobile / natif : image téléphone inchangée.
  const isWideWeb = isWeb && !isMobile
  const heroImage = isWideWeb ? finalStepRegisterWeb : finalStepRegister
  const styles = getStyle(theme, top)

  const { data: profile, refetch } = useGetProfile()
  const { setUserID } = useCurrentUserStore()
  const shareMyLocation = useShareMyLocation()

  const [isGeolocationEnabled, setIsGeolocationEnabled] = useState(false)
  // The position obtained when the box was ticked: saved when "Suivant" is
  // pressed, so the box decides whether sign-up stores a position.
  const coordsRef = useRef<Coords | null>(null)
  // Phone apps only: the position being read after the box was pressed. The
  // website keeps its behaviour.
  const pendingPositionRef = useRef<Promise<Coords | null> | null>(null)
  const [isLocating, setIsLocating] = useState(false)
  const [isFinishing, setIsFinishing] = useState(false)
  const { animatedValues, registerAnimation } = useAnimatedValues([0, 0]) // [scale, opacity]
  const [scaleAnim, opacityAnim] = animatedValues

  useEffect(() => {
    if (isGeolocationEnabled) {
      const animations = Animated.parallel([
        registerAnimation(Animated.spring(scaleAnim, {
          toValue: 1,
          useNativeDriver: true,
          tension: 150,
          friction: 4,
        })),
        registerAnimation(Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        })),
      ])
      animations.start()
    } else {
      const animations = Animated.parallel([
        registerAnimation(Animated.timing(scaleAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        })),
        registerAnimation(Animated.timing(opacityAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        })),
      ])
      animations.start()
    }
  }, [isGeolocationEnabled, scaleAnim, opacityAnim, registerAnimation])

  /**
   * Web: navigator.geolocation prompts the browser; react-native-permissions does not support web.
   * Native: expo-location (via getCurrentPosition) requests foreground permission — same stack as the rest of the app.
   */
  const requestLocationPermission = async () => {
    try {
      const request = getCurrentPosition()
      if (Platform.OS !== 'web') {
        pendingPositionRef.current = request
        setIsLocating(true)
      }
      const coords = await request
      if (Platform.OS !== 'web') {
        // The box was pressed again meanwhile: this answer is no longer wanted.
        if (pendingPositionRef.current !== request) return
        pendingPositionRef.current = null
        setIsLocating(false)
      }
      if (coords) {
        coordsRef.current = coords
        setIsGeolocationEnabled(true)
        return
      }

      setIsGeolocationEnabled(false)

      if (Platform.OS === 'web') {
        return
      }

      Alert.alert(
        t('geolocation.denied.title'),
        t('geolocation.denied.message'),
        [
          {
            text: t('geolocation.denied.cancel'),
            style: 'cancel',
          },
          {
            text: t('geolocation.denied.settings'),
            onPress: () => Linking.openSettings?.(),
          },
        ],
      )
    } catch (error) {
      console.error('Erreur lors de la demande de permission de géolocalisation:', error)
      setIsGeolocationEnabled(false)
    }
  }

  const handleGeolocationToggle = () => {
    if (isLocating) {
      // Pressed again while the position is being read: the box is unticked.
      pendingPositionRef.current = null
      setIsLocating(false)
      return
    }
    if (!isGeolocationEnabled) {
      requestLocationPermission()
    } else {
      coordsRef.current = null
      setIsGeolocationEnabled(false)
    }
  }

  const handleFinish = async () => {
    // The profile may not be loaded yet (slow network): ask for it again
    // rather than leaving the button without effect.
    const accountId = profile?.accountId ?? (await refetch()).data?.accountId
    if (!accountId) {
      showErrorToast(t('errors.title'), t('errors.generic'))
      return
    }
    const pending = pendingPositionRef.current
    if (pending) {
      setIsFinishing(true)
      const isTooLate = await Promise.race([
        pending.then(() => false),
        new Promise<boolean>((resolve) => setTimeout(() => resolve(true), POSITION_MAX_WAIT_MS)),
      ])
      setIsFinishing(false)
      if (isTooLate && pendingPositionRef.current === pending) {
        // The app opens now. This screen no longer wants the answer, so a
        // position the phone fails to give shows no alert over the app later;
        // a position that does arrive is still saved. Not when the box was
        // unticked during the wait: nothing is saved then.
        pendingPositionRef.current = null
        setIsLocating(false)
        void pending.then((coords) => {
          if (coords) void shareMyLocation(accountId, coords)
        })
      }
    }
    // coordsRef is only filled while the box is ticked.
    if (coordsRef.current) {
      void shareMyLocation(accountId, coordsRef.current)
    }
    setUserID(accountId)
    navigate('Tab')
  }

  const content = (
      <ImageBackground source={heroImage} style={styles.finalContainer} resizeMode="cover">
        <OnBoarding>
          <View style={styles.finalTitleContainer}>
            <Text
              type="headline"
              color={theme.colors.white}
              style={isWideWeb ? [styles.finalTitle, styles.finalTitleWeb] : styles.finalTitle}
            >
              {profile?.firstName || profile?.lastName
                ? t('registerFinalStep.congrats', {
                    name: profile?.firstName ?? '',
                    surname: profile?.lastName ?? '',
                  })
                : t('registerFinalStep.congratsNoName')}
            </Text>
          </View>
          <View style={styles.geolocationCard}>
            <TouchableOpacity 
              style={styles.checkboxContainer}
              onPress={handleGeolocationToggle}
            >
              <View style={[
                styles.checkbox, 
                isGeolocationEnabled && styles.checkboxSelected
              ]}>
                <Animated.View
                  style={{
                    position: 'absolute',
                    transform: [{ scale: scaleAnim }],
                    opacity: opacityAnim,
                  }}
                >
                  <Icon 
                    name="check" 
                    color={theme.colors.white} 
                    size={theme.iconSize.extraSmall}
                  />
                </Animated.View>
                {isLocating && (
                  <ActivityIndicator
                    testID="final-step-locating"
                    size="small"
                    color={theme.colors.primary}
                    style={styles.checkboxSpinner}
                  />
                )}
              </View>
              <Text type="body" style={styles.geolocationText}>
                {t('registerFinalStep.geolocationText')}
              </Text>
            </TouchableOpacity>
          </View>

          <Button
            title={t('registerFinalStep.next')}
            containerStyle={styles.finalButton}
            onPress={handleFinish}
            disabled={isFinishing}
          />
        </OnBoarding>
      </ImageBackground>
  )

  return (
    <Screen noPaddingTop noPaddingBottom>
      {content}
    </Screen>
  )
}
